import React from 'react';
import { Checkbox, Field, Input } from '@milanote/roma';
import type { CheckboxProps } from '@milanote/roma';
import { FormatOptions } from '../../../../common/table/CellTypeConstants';
import type {
    CellTypeFormatChanges,
    CurrencyTypeObject,
    NumberTypeObject,
    PercentageTypeObject,
} from '../../../../common/table/TableTypes';

import { useTranslator } from '../../../components/i18n/useTranslator';

type DecimalCellTypeObject = NumberTypeObject | CurrencyTypeObject | PercentageTypeObject;

type Props<T extends DecimalCellTypeObject = DecimalCellTypeObject> = {
    cellTypeObject: T;
    updateCellTypeFormat: (cellTypeFormatChanges: CellTypeFormatChanges) => void;
    formSize: CheckboxProps['size'];
};

// max allowed value for minimumFractionDigits and maximumFractionDigits https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#minimumfractiondigits
const MAX_DECIMALS = 20;

const DecimalFormatOptions = <T extends DecimalCellTypeObject>({
    cellTypeObject,
    updateCellTypeFormat,
    formSize,
}: Props<T>): React.ReactElement => {
    const translate = useTranslator('popup', 'table.format');

    const { decimals, thousandsSeparator } = cellTypeObject;

    const isAutoDecimal = decimals === 'auto';

    const showAccounting = FormatOptions.ACCOUNTING in cellTypeObject;

    const onDecimalsChange = React.useCallback(
        (targetValue: number): void => {
            if (!Number.isFinite(targetValue)) {
                updateCellTypeFormat({ [FormatOptions.DECIMALS]: 'auto' });
                return;
            }

            // Roma starts empty step-down actions from maxValue, but auto should stay auto when decreasing.
            if (isAutoDecimal && targetValue === MAX_DECIMALS) return;
            if (isAutoDecimal && targetValue < 0) return;
            if (targetValue > MAX_DECIMALS) return;

            if (decimals === 0 && targetValue === -1) {
                updateCellTypeFormat({ [FormatOptions.DECIMALS]: 'auto' });
                return;
            }

            updateCellTypeFormat({ [FormatOptions.DECIMALS]: targetValue });
        },
        [decimals, isAutoDecimal, updateCellTypeFormat],
    );

    return (
        <div className="DecimalFormatOptions celltype-format-section">
            <Field.Label className="celltype-format-title" size={formSize}>
                {translate('decimals')}
            </Field.Label>
            <div className="celltype-format-inputs">
                <div className="celltype-format-input">
                    <Input.Number
                        size={formSize}
                        value={isAutoDecimal ? NaN : decimals}
                        maxValue={MAX_DECIMALS}
                        minValue={isAutoDecimal ? 0 : -1} // need -1 here to allow user to click/arrow down from 0 to get auto
                        onChange={onDecimalsChange}
                        placeholder={translate('decimalsPlaceholder')}
                    />
                </div>

                <div className="checkbox-input celltype-format-input">
                    <Checkbox
                        label={translate('thousandsSeparator')}
                        size={formSize}
                        variant="primary"
                        isSelected={thousandsSeparator}
                        onChange={(nextChecked) =>
                            updateCellTypeFormat({ [FormatOptions.THOUSANDS_SEPARATOR]: nextChecked })
                        }
                    />
                </div>
                {showAccounting && (
                    <div className="checkbox-input celltype-format-input">
                        <Checkbox
                            label={translate('accountingStyle')}
                            size={formSize}
                            variant="primary"
                            isSelected={cellTypeObject[FormatOptions.ACCOUNTING]}
                            onChange={(nextChecked) =>
                                updateCellTypeFormat({ [FormatOptions.ACCOUNTING]: nextChecked })
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DecimalFormatOptions;
